import { HttpClient } from '@angular/common/http';
import { Injectable, linkedSignal, Signal, signal, WritableSignal } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '@environments/environment';
import { BehaviorSubject, Observable } from 'rxjs';
import { CategoryName } from 'src/app/models/enums/CategoryName.enum';
import { RuleEnum } from 'src/app/models/enums/Rule.enum';
import { Route } from '../../models/enums/Route.enum';
import { AddOn, Categories, Category, Node, Nodes, Rule, Rules, RulesMap, TabRule } from '../../models/types/Rule/Rule.type';
import { LoaderService } from '../loader.service';
import { SharedService } from '../shared.service';
import { DynatraceService } from '../dynatrace.service';

@Injectable({
  providedIn: 'root'
})
export class RulesService {

  private readonly apiUrl = `${environment.apiUrl}/Rules`;

  private _rules: Rules = [];
  public rulesSubject = new BehaviorSubject<Rules>([]);
  public rules$ = this.rulesSubject.asObservable();
  public get rules(): Rules {
    return this._rules;
  }
  public get areRulesLoaded(): boolean {
    return this.rules.length > 0;
  }

  private _rulesMap: RulesMap = new Map();
  public get rulesMap(): RulesMap {
    return this._rulesMap;
  }

  private _selectedRule: WritableSignal<Rule | undefined> = signal(undefined);
  public selectedRule: Signal<Rule | undefined> = linkedSignal(() => this._selectedRule());
  public get selectedRuleIndex(): number {
    return this.rules.findIndex(rule => rule.id === this.selectedRule()?.id);
  }
  public get selectedRuleId(): number | undefined {
    return this.selectedRule()?.id;
  }

  // private rulesTree

  constructor(
    private readonly loaderService: LoaderService,
    private readonly sharedService: SharedService,
    private readonly dynatraceService: DynatraceService,
    private readonly router: Router,
    private readonly http: HttpClient,
  ) {
    // this.loadRules();
  }

  loadRules() {
    this.loaderService.show();
    this.getRules().subscribe(
      {
        next: (rules) => {
          this.buildRulesMap(rules as Nodes);
          this._rules = this.parseRules(rules);
          this.rulesSubject.next(this._rules);
          this.loaderService.hide();
        },
        error: (e) => {
          console.log(e);
          //Se ho riscontrato un errore nel reperimento delle rules riporto alla pagina di errore
          this.sharedService.navigateTo_ErrorPage(() => console.log('error_page'));
          this.loaderService.hide();
        },
      }
    )
  }

  /**
   * Metodo ricorsivo che costruisce la mappa delle RULES in modo tale che ogni rule, categoria,
   * sottocategoria, ecc... siano contenute in questa mappa.
   * Ogni elemento di questa mappa avrà un parametro "parentId" che indica l'id dell'elemento della mappa
   * di cui è figlio.
   * Tutto ciò per avere accesso diretto alle rules.
   *
   * @param elements
   * @param parent
   */
  buildRulesMap(elements: Nodes, parent?: Node) {
    elements.forEach(elem => {
      let id = elem.id;
      let parentId = parent ? parent.id : null;
      let node: Node = {
        ...elem,
        parentId: parentId
      }
      this._rulesMap.set(id, node);
      let subElements: Nodes = [];
      if (elem.discoverCategories?.length > 0) {
        subElements = subElements.concat(elem.discoverCategories as Nodes);
      }
      if (elem.subCategories?.length > 0) {
        subElements = subElements.concat(elem.subCategories as Nodes);
      }
      if (elem.childs?.length > 0) {
        subElements = subElements.concat(elem.childs as Nodes);
      }
      if (subElements.length > 0) {
        this.buildRulesMap(subElements, elem);
      }
    })
  }

  /**
   * Fa un parse delle rules
   *
   * @param rules
   * @returns
   */
  parseRules(rules: Rules) {
    return rules.map(rule => {
      let { name, assetId } = rule;
      let assetSrc = '';
      switch (name) {
        case RuleEnum.JEWELRY:
          assetSrc = 'assets/images/rules/jewelry.png';
          break;
        case RuleEnum.WATCHES:
          assetSrc = 'assets/images/rules/watches.png';
          break;
        case RuleEnum.LEATHER_GOODS_ACCESSORIES:
          assetSrc = 'assets/images/rules/leather_goods_accessories.png';
          break;
        case RuleEnum.BRIDAL:
          assetSrc = 'assets/images/rules/bridal.png';
          break;
        case RuleEnum.HIGH_JEWELRY:
          assetSrc = 'assets/images/rules/high_jewelry.png';
          break;
        case RuleEnum.HIGH_END_WATCHES:
          assetSrc = 'assets/images/rules/high_end_watches.png';
          break;
        case RuleEnum.HIGH_END_PERFUMES:
          assetSrc = 'assets/images/rules/high_end_perfumes.png';
          break;
      }
      return {
        ...rule,
        assetSrc,
      };
    });
  }

  getRules(): Observable<Rules> {
    const url = `${this.apiUrl}`;
    return (this.http.get(url) as Observable<Rules>);
  }

  getRuleById(id: number): Rule | undefined {
    return this._rules.find(rule => rule.id === id);
  }

  setSelectedRule(rule: Rule) {
    this._selectedRule.set(rule);
    this.dynatraceService.logRuleName(rule);
  }

  resetSelectedRule() {
    this._selectedRule.set(undefined);
  }

  /**
   * Funzione che controlla la validità della rule letta dall'URL.
   * Se il parametro è null la rule viene resettata
   * Se il parametro è una rule valida si procede al settaggio
   * Altrimenti si viene rindirizzati alla pagina di selezione delle Rule
   *
   * @param rule parametro letto dall'url
   * @param callback eventuale callback da implementare dopo il check
   */
  loadSelectedRule(rule: string | null, callback?: Function) {
    if (!rule) {
      this.resetSelectedRule();
    } else if (this.isIdInRulesMap(rule)) {
      this.setSelectedRuleByUrlParam(rule!);
    } else {
      this.router.navigate([Route.Main, Route.RulePage]);
    }
    if (callback) {
      callback();
    }
  }

  /**
   * Setta la rule "selectedRule" in base a quella ottenuta nell'URL.
   *
   * @param id - Rule ottenuta come parametro dall'URL.
   */
  setSelectedRuleByUrlParam(id: string) {
    if (this.areRulesLoaded) {
      let rule = this.rulesMap.get(parseInt(id));
      if (rule) {
        this.setSelectedRule(rule);
      }
    }
  }

  /**
   * Controlla se il parametro in ingresso sia un rule valida presente tra quelle caricate.
   *
   * @param id
   * @returns Vero o falso se il parametro è una "rule" valida
   */
  isIdInRulesMap(id: string | null): boolean {
    if (!id) {
      return false;
    }
    let rule = this._rulesMap.get(parseInt(id))
    return !!rule;
  }

  /**
   * Ritorna l'oggetto AddOn della category passata come input
   *
   * @param id
   */
  getAddOn(id?: number): AddOn | null {
    if (!id) {
      return null;
    }
    let rule = this.rulesMap.get(id);
    if (rule && rule.addOn) {
      return rule.addOn;
    }
    return null;
  }

  /**
   * Ritorna TRUE se l'addOn deve portare alla pagina AddOn, altrimenti apre direttamente la modale con i PDF
   *
   * @param addOn
   * @returns
   */
  shouldAddOnOpenPdfModal(addOn: AddOn | null): boolean {
    if (!addOn) {
      return false;
    }
    let { mediaVideo, mediaPdf } = addOn;
    return Boolean(mediaVideo && mediaVideo?.length === 0 && mediaPdf && mediaPdf?.length > 0);

  }

  /**
   * Funzione che ritorna un array di link in base alle rules.
   *
   * @returns Array di link in base all'array rules
   */
  getTabRules(): TabRule[] {
    return this.rules.map((rule) => {
      let { id, name } = rule;
      let routerLink = this.getRulePageUrl(rule);
      return ({ id, name, routerLink } as TabRule);
    });
  }

  /**
   * Funzione che ritorna l'URL della RULE passata come parametro
   *
   * @param rule Rule di cui ottenere l'URL
   * @returns L'URL della RULE
   */
  getRulePageUrl(rule: Rule): string {
    return `/${Route.Main}/${Route.RulePage}/${rule.id}`;
  }

  /**
   * Funzione che ritorna l'URL della CATEGORY passata come parametro
   *
   * @param rule
   * @param category
   * @returns
   */
  getCategoryPageUrl(category: Category) {
    let rule = this.selectedRule();
    if (rule) {
      return `/${this.getRulePageUrl(rule)}/${category.id}`
    } else {
      return `/${Route.Main}/${Route.RulePage}`
    }
  }

  /**
   * Funzione che ritorna TRUE se la rule passata come parametro è di tipo HIGH END WATCHES
   *
   * @param rule La rule da verificare
   * @returns il flag che determina se la rule è "HIGH END WATCHES"
   */
  isHighEndWatchesRule(rule: Rule | undefined): boolean {
    let isHighEndWatchesRule = false;
    if (rule) {
      if (rule.discoverCategories.length === 0) {
        if (rule.subCategories.length > 0) {
          isHighEndWatchesRule = true;
        }
      }
    }
    return isHighEndWatchesRule;
  }

  /**
   * Funzione che ritorna TURE se la rule passata come parametro è di tipo HIGH JEWELRY
   *
   * @param rule La rule da verificare
   * @returns il flag che determina se la rule è "HIGH JEWELRY"
   */
  isHighJewelry(rule: Rule | undefined): boolean {
    let isHighJewelry = false;
    if (rule) {
      if (rule.discoverCategories.find(category => category.name.trim().toLowerCase().includes("main stone"))) {
        isHighJewelry = true;
      }
    }
    return isHighJewelry;
  }

  /**
   * Funzione che ritorna TRUE se il tipo di rule selezionata è HIGH JEWELTY o HIGH END WATCHES
   */
  isHJorHEWrule() {
    let rule = this.selectedRule();
    if (rule) {
      return this.isHighEndWatchesRule(rule) || this.isHighJewelry(rule);
    }
    return false;
  }

  getDiscoverByCategory(rule: Rule | undefined) {
    return rule?.discoverCategories.find(cat => cat.name.includes(CategoryName.DiscoverByCategory))
  }

  getCategoryHeader(rule: Rule | undefined): Categories | undefined {
    let cat: Categories | undefined;
    switch (rule?.name) {
      case RuleEnum.JEWELRY:
      case RuleEnum.WATCHES: {
        cat = rule.discoverCategories[0];
        break;
      }
      case RuleEnum.HIGH_JEWELRY: {
        cat = rule.discoverCategories[1];
        break;
      }
      case RuleEnum.LEATHER_GOODS_ACCESSORIES:
      case RuleEnum.BRIDAL:
      case RuleEnum.HIGH_END_WATCHES:
      case RuleEnum.HIGH_END_PERFUMES: {
        break;
      }
    }
    return cat;
  }

  getCategoryBody(rule: Rule | undefined): Categories[] | undefined {
    let cat: Categories[] | undefined;
    switch (rule?.name) {
      case RuleEnum.JEWELRY:
      case RuleEnum.WATCHES: {
        cat = [rule.discoverCategories[1]];
        break;
      }
      case RuleEnum.LEATHER_GOODS_ACCESSORIES:
      case RuleEnum.BRIDAL:
      case RuleEnum.HIGH_END_PERFUMES: {
        cat = [...rule.discoverCategories];
        break;
      }
      case RuleEnum.HIGH_JEWELRY: {
        //Rimuovo la categoria "Discover by Main Stone"
        let categories = [...rule.discoverCategories];
        categories.splice(1, 1);
        cat = [...categories];
        break;
      }
      case RuleEnum.HIGH_END_WATCHES: { break; }
    }
    return cat;
  }
}
